import { useState, useEffect, useRef, useContext } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase.config";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import LoadingContext from "../contexts/loading/LoadingContext";

import Spinner from "../components/feedback/Spinner";
import { 
  Box, 
  Container, 
  VStack, 
  HStack, 
  Heading, 
  Text,
  Card,
  CardBody,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepTitle,
  StepDescription,
  StepSeparator,
  StepNumber,
  useSteps,
  Divider,
  Alert,
  AlertIcon,
  SimpleGrid,
  Icon,
  Tooltip,
  Progress,
  Badge,
  useMediaQuery,
} from "@chakra-ui/react";
import { 
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  Input,
  NumberInput,
  Textarea,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Radio,
  RadioGroup,
  Stack,
  Button,
  useToast,
  Checkbox,
  Select
} from "@chakra-ui/react";
import { 
  FaCar, 
  FaMoneyBillWave, 
  FaMapMarkerAlt, 
  FaImage, 
  FaInfoCircle, 
  FaArrowLeft, 
  FaArrowRight,
  FaUsers,
  FaCalendarAlt,
  FaClock,
  FaRoute,
  FaExchangeAlt
} from "react-icons/fa";

import { carsData } from "../data/carsData";
import { cities } from "../data/citiesData";
import { colorsData } from "../data/colorsData";
import { Helmet } from "react-helmet";

// Pakistan major cities for ride sharing
const pakistanCities = [
  "Karachi", "Lahore", "Islamabad", "Rawalpindi", "Faisalabad",
  "Multan", "Peshawar", "Quetta", "Gujranwala", "Sialkot",
  "Hyderabad", "Abbottabad", "Bahawalpur", "Sargodha", "Sukkur", "Swat", "Gilgit", "Skardu", "Muzaffarabad", "Mardan", "Taxila", "Dir", "Chitral", "Nowshehra", "Swabi", "Malakand"
];

const steps = [
  { title: "Basic Info", description: "Car & Route details", icon: FaCar },
  { title: "Pricing & Schedule", description: "Set your price", icon: FaMoneyBillWave },
  { title: "Location", description: "Pickup point", icon: FaMapMarkerAlt },
  { title: "Extras", description: "Options & images", icon: FaImage },
];

const CreateListing = () => {
  const [error, setError] = useState("");
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const [formData, setFormData] = useState({
    type: "sale",
    manufacturer: "Honda",
    modelName: "",
    offer: false,
    transmission: "Automatic",
    modelYear: "2022",
    color: "White",
    location: "",
    city: "Karachi",
    mileage: 0,
    price: 0,
    discountedPrice: 0,
    notes: "",
    images: {},
    phoneNumber: "",
    options: {
      leatherSeats: false,
      airBags: false,
      bluetooth: false,
      fogLights: false,
      ac: true,
      usbPort: false,
    },
    // Sale specific - Installments
    installmentsAvailable: false,
    downPayment: 0,
    monthlyInstallment: 0,
    installmentPeriod: 12,
    // Sale specific - Exchange
    exchangeAvailable: false,
    exchangeManufacturer: "",
    exchangeModel: "",
    exchangeYear: "",
    exchangeCondition: "good",
    exchangeMileage: "",
    userRef: "",
    // Ride sharing specific fields
    fromCity: "",
    toCity: "",
    availableSeats: 4,
    departureDate: "",
    departureTime: "",
    rideType: "one-way",
    luggageSpace: "Medium",
    smoking: false,
    pets: false,
    femaleOnly: false,
    returnDate: "",
    returnTime: "",
    pickupLocation: "", // Specific pickup address/landmark
  });

  const { buttonLoading, spinnerLoading, dispatch } = useContext(LoadingContext);
  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });
  const toast = useToast();
  const isMounted = useRef(true);
  const auth = getAuth();
  const navigate = useNavigate();

  const formatPrice = (val) => `₨ ` + (val || 0).toLocaleString();
  const parsePrice = (val) => parseInt(String(val).replace(/[^0-9]/g, "")) || 0;

  useEffect(() => {
    if (isMounted.current) {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          setFormData((prev) => ({
            ...prev,
            userRef: user.uid,
          }));
          dispatch({ type: "STOP_SPINNER_LOADING" });
        } else {
          navigate("/sign-in");
        }
      });
    }

    return () => {
      isMounted.current = false;
    };
  }, [isMounted, auth, navigate, dispatch]);

  const changeHandler = (e) => {
    if (!e.target.files) {
      setFormData((prev) => ({
        ...prev,
        [e.target.id]: e.target.value,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        images: e.target.files,
      }));
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Basic Info
        if (formData.type === "ride") {
          const hasFromCity = formData.fromCity && formData.fromCity.trim() !== "";
          const hasToCity = formData.toCity && formData.toCity.trim() !== "";
          const hasPhone = formData.phoneNumber && formData.phoneNumber.trim() !== "";
          return hasFromCity && hasToCity && hasPhone;
        } else {
          const hasModel = formData.modelName && formData.modelName.trim() !== "";
          const hasYear = formData.modelYear && formData.modelYear.toString().trim() !== "";
          const hasPhone = formData.phoneNumber && formData.phoneNumber.trim() !== "";
          return hasModel && hasYear && hasPhone;
        }
      case 1: // Pricing & Route
        if (!formData.price || formData.price <= 0) return false;
        if (formData.type === "ride") {
          const hasFromCity = formData.fromCity && formData.fromCity.trim() !== "";
          const hasToCity = formData.toCity && formData.toCity.trim() !== "";
          const hasSeats = formData.availableSeats && formData.availableSeats > 0;
          const hasDate = formData.departureDate && formData.departureDate.trim() !== "";
          const hasTime = formData.departureTime && formData.departureTime.trim() !== "";
          return hasFromCity && hasToCity && hasSeats && hasDate && hasTime;
        }
        if (formData.type === "sale") {
          if (formData.installmentsAvailable && (!formData.downPayment || !formData.monthlyInstallment)) {
            return false;
          }
          if (formData.exchangeAvailable && !formData.exchangeManufacturer) {
            return false;
          }
        }
        return true;
      case 2: // Location
        if (formData.type === "ride") {
          // For ride, only need pickup location (specific address/landmark)
          const hasPickupLocation = formData.pickupLocation && formData.pickupLocation.trim() !== "";
          return hasPickupLocation;
        } else {
          const hasCity = formData.city && formData.city.trim() !== "";
          const hasLocation = formData.location && formData.location.trim() !== "";
          return hasCity && hasLocation;
        }
      case 3: // Extras
        return formData.images && Object.keys(formData.images).length > 0;
      default:
        return true;
    }
  };

  const nextStep = () => {
    const isValid = validateStep(activeStep);
    
    if (isValid) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    } else {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields before proceeding.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!validateStep(0) || !validateStep(1) || !validateStep(2) || !validateStep(3)) {
      toast({
        title: "Incomplete Form",
        description: "Please fill in all required fields across all steps.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (formData.type === "sale") {
      if (formData.installmentsAvailable && (!formData.downPayment || !formData.monthlyInstallment)) {
        toast({
          title: "Missing Installment Details",
          description: "Down payment and monthly installment are required.",
          status: "warning",
          duration: 3000,
        });
        return;
      }
      if (formData.exchangeAvailable && !formData.exchangeManufacturer) {
        toast({
          title: "Missing Exchange Details",
          description: "Please specify what car model you're looking for in exchange.",
          status: "warning",
          duration: 3000,
        });
        return;
      }
    }

    dispatch({ type: "START_LOADING" });

    try {
      const storeImage = async (img) => {
        return new Promise((resolve, reject) => {
          const storage = getStorage();
          const fileName = `${auth.currentUser.uid}-${img.name}-${uuidv4()}`;
          const storageRef = ref(storage, "images/" + fileName);
          const uploadTask = uploadBytesResumable(storageRef, img);

          uploadTask.on(
            "state_changed",
            () => {},
            reject,
            () => {
              getDownloadURL(uploadTask.snapshot.ref).then(resolve);
            }
          );
        });
      };

      const carImages = await Promise.all(
        [...formData.images].map((img) => storeImage(img))
      );

      const formDataCopy = {
        ...formData,
        carImages,
        timestamp: serverTimestamp(),
      };

      delete formDataCopy.images;

      const docRef = await addDoc(collection(db, "cars"), formDataCopy);

      dispatch({ type: "STOP_LOADING" });
      toast({
        title: "Success!",
        description: formData.type === "ride" 
          ? "Your ride has been posted successfully" 
          : "Your advertisement has been added successfully",
        status: "success",
        position: "top",
        duration: 3000,
        isClosable: true,
      });

      navigate(`/category/${formDataCopy.type}/${docRef.id}`);
    } catch (error) {
      console.error(error);
      dispatch({ type: "STOP_LOADING" });
      toast({
        title: "Error",
        description: "Something went wrong! Please try again.",
        status: "error",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (spinnerLoading) return <Spinner />;

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="lg" bg="blue.50" borderLeft="4px solid" borderLeftColor="blue.500">
              <AlertIcon color="blue.500" />
              {formData.type === "ride" 
                ? "Fill in your ride route and contact information" 
                : "Fill in the basic details of your vehicle"}
            </Alert>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {/* For Ride Sharing - Show From/To Cities in Step 0 */}
              {formData.type === "ride" && (
                <>
                  <FormControl isRequired>
                    <FormLabel display="flex" alignItems="center" gap={2}>
                      <Icon as={FaMapMarkerAlt} color="blue.500" />
                      From City (Pickup City)
                    </FormLabel>
                    <Select
                      value={formData.fromCity}
                      onChange={(e) => setFormData(prev => ({ ...prev, fromCity: e.target.value }))}
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                      placeholder="Select departure city"
                    >
                      <option value="">Select departure city</option>
                      {pakistanCities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </Select>
                    <FormHelperText>Where will your ride start? (Pickup city)</FormHelperText>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel display="flex" alignItems="center" gap={2}>
                      <Icon as={FaRoute} color="blue.500" />
                      To City (Destination)
                    </FormLabel>
                    <Select
                      value={formData.toCity}
                      onChange={(e) => setFormData(prev => ({ ...prev, toCity: e.target.value }))}
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                      placeholder="Select destination city"
                    >
                      <option value="">Select destination city</option>
                      {pakistanCities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </Select>
                    <FormHelperText>Where is your ride going?</FormHelperText>
                  </FormControl>
                </>
              )}

              {/* For Sale/Rent - Show Manufacturer and Model */}
              {formData.type !== "ride" && (
                <>
                  <FormControl isRequired>
                    <FormLabel display="flex" alignItems="center" gap={2}>
                      Manufacturer
                      <Tooltip label="Select car manufacturer">
                        <Icon as={FaInfoCircle} color="gray.500" boxSize={3} />
                      </Tooltip>
                    </FormLabel>
                    <Select
                      id="manufacturer"
                      value={formData.manufacturer}
                      onChange={changeHandler}
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                    >
                      {carsData.map((car) => (
                        <option key={car.manufacturer} value={car.manufacturer}>
                          {car.manufacturer}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Model Name</FormLabel>
                    <Input
                      id="modelName"
                      value={formData.modelName}
                      onChange={changeHandler}
                      placeholder="e.g., Civic 2022"
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                    />
                  </FormControl>
                </>
              )}

              <FormControl>
                <FormLabel>Color</FormLabel>
                <Select
                  id="color"
                  value={formData.color}
                  onChange={changeHandler}
                  variant="filled"
                  size="lg"
                  focusBorderColor="blue.500"
                >
                  {colorsData.map((color) => (
                    <option key={color.color} value={color.color}>
                      {color.color}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {formData.type !== "ride" && (
                <FormControl>
                  <FormLabel>Transmission</FormLabel>
                  <RadioGroup
                    value={formData.transmission}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, transmission: value }))
                    }
                  >
                    <Stack direction="row" spacing={4}>
                      <Radio value="Manual" colorScheme="blue">Manual</Radio>
                      <Radio value="Automatic" colorScheme="blue">Automatic</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>
              )}

              {formData.type !== "ride" && (
                <FormControl isRequired>
                  <FormLabel>Model Year</FormLabel>
                  <NumberInput
                    min={1920}
                    max={2024}
                    value={formData.modelYear}
                    onChange={(valueString) =>
                      setFormData((prev) => ({ ...prev, modelYear: valueString }))
                    }
                    variant="filled"
                    size="lg"
                  >
                    <NumberInputField focusBorderColor="blue.500" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              )}

              {formData.type !== "ride" && (
                <FormControl isRequired>
                  <FormLabel>Mileage (km)</FormLabel>
                  <NumberInput
                    min={0}
                    value={formData.mileage}
                    onChange={(valueString) =>
                      setFormData((prev) => ({ ...prev, mileage: valueString }))
                    }
                    variant="filled"
                    size="lg"
                  >
                    <NumberInputField focusBorderColor="blue.500" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              )}
            </SimpleGrid>

            <FormControl isRequired>
              <FormLabel>Phone Number</FormLabel>
              <Input
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={changeHandler}
                placeholder="+92 XXX XXXXXXX"
                variant="filled"
                size="lg"
                focusBorderColor="blue.500"
              />
              <FormHelperText>This will be visible to potential riders/buyers</FormHelperText>
            </FormControl>
          </VStack>
        );

      case 1:
        return (
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="lg" bg="blue.50" borderLeft="4px solid" borderLeftColor="blue.500">
              <AlertIcon color="blue.500" />
              {formData.type === "ride" 
                ? "Confirm your route, set schedule, and pricing for ride sharing" 
                : "Set your pricing and listing type"}
            </Alert>

            <FormControl>
              <FormLabel>Listing Type</FormLabel>
              <RadioGroup
                value={formData.type}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, type: value }))
                }
              >
                <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                  <Card 
                    onClick={() => setFormData(prev => ({...prev, type: "sale"}))}
                    border={formData.type === "sale" ? "2px solid" : "1px solid"}
                    borderColor={formData.type === "sale" ? "blue.500" : "gray.200"}
                    cursor="pointer"
                    _hover={{ borderColor: "blue.400", transform: "translateY(-2px)" }}
                    transition="all 0.2s"
                    bg={formData.type === "sale" ? "blue.50" : "white"}
                  >
                    <CardBody>
                      <Icon as={FaCar} boxSize={6} color="blue.600" mb={2} />
                      <Text fontWeight="bold">For Sale</Text>
                      <Text fontSize="sm" color="gray.600">Sell your car directly</Text>
                    </CardBody>
                  </Card>
                  <Card 
                    onClick={() => setFormData(prev => ({...prev, type: "rent"}))}
                    border={formData.type === "rent" ? "2px solid" : "1px solid"}
                    borderColor={formData.type === "rent" ? "blue.500" : "gray.200"}
                    cursor="pointer"
                    _hover={{ borderColor: "blue.400", transform: "translateY(-2px)" }}
                    transition="all 0.2s"
                    bg={formData.type === "rent" ? "blue.50" : "white"}
                  >
                    <CardBody>
                      <Icon as={FaClock} boxSize={6} color="blue.600" mb={2} />
                      <Text fontWeight="bold">For Rent</Text>
                      <Text fontSize="sm" color="gray.600">Rent by day</Text>
                    </CardBody>
                  </Card>
                  <Card 
                    onClick={() => setFormData(prev => ({...prev, type: "ride"}))}
                    border={formData.type === "ride" ? "2px solid" : "1px solid"}
                    borderColor={formData.type === "ride" ? "blue.500" : "gray.200"}
                    cursor="pointer"
                    _hover={{ borderColor: "blue.400", transform: "translateY(-2px)" }}
                    transition="all 0.2s"
                    bg={formData.type === "ride" ? "blue.50" : "white"}
                  >
                    <CardBody>
                      <Icon as={FaUsers} boxSize={6} color="blue.600" mb={2} />
                      <Text fontWeight="bold">Ride Sharing</Text>
                      <Text fontSize="sm" color="gray.600">Carpool across cities</Text>
                    </CardBody>
                  </Card>
                </Stack>
              </RadioGroup>
            </FormControl>

            {formData.type === "ride" ? (
              // Ride Sharing Form
              <VStack spacing={6} align="stretch">
                {/* Route Summary */}
                <Card bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
                  <Text fontWeight="bold" mb={3} display="flex" alignItems="center" gap={2}>
                    <Icon as={FaRoute} color="blue.600" />
                    Your Route
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel display="flex" alignItems="center" gap={2}>
                        <Icon as={FaMapMarkerAlt} color="blue.500" />
                        From City (Pickup)
                      </FormLabel>
                      <Select
                        value={formData.fromCity}
                        onChange={(e) => setFormData(prev => ({ ...prev, fromCity: e.target.value }))}
                        variant="filled"
                        size="lg"
                        focusBorderColor="blue.500"
                        placeholder="Select departure city"
                      >
                        <option value="">Select departure city</option>
                        {pakistanCities.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </Select>
                      <FormHelperText>Pickup city for passengers</FormHelperText>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel display="flex" alignItems="center" gap={2}>
                        <Icon as={FaRoute} color="blue.500" />
                        To City (Destination)
                      </FormLabel>
                      <Select
                        value={formData.toCity}
                        onChange={(e) => setFormData(prev => ({ ...prev, toCity: e.target.value }))}
                        variant="filled"
                        size="lg"
                        focusBorderColor="blue.500"
                        placeholder="Select destination city"
                      >
                        <option value="">Select destination city</option>
                        {pakistanCities.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </Select>
                      <FormHelperText>Destination city</FormHelperText>
                    </FormControl>
                  </SimpleGrid>
                  
                  {/* Swap button */}
                  {formData.fromCity && formData.toCity && (
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      mt={2}
                      onClick={() => {
                        const temp = formData.fromCity;
                        setFormData(prev => ({
                          ...prev,
                          fromCity: prev.toCity,
                          toCity: temp
                        }));
                      }}
                    >
                      <Icon as={FaExchangeAlt} mr={1} /> Swap Cities
                    </Button>
                  )}
                </Card>

                {/* Ride Type */}
                <FormControl isRequired>
                  <FormLabel>Ride Type</FormLabel>
                  <RadioGroup
                    value={formData.rideType}
                    onChange={(value) => setFormData(prev => ({ ...prev, rideType: value }))}
                  >
                    <Stack direction="row" spacing={4}>
                      <Radio value="one-way" colorScheme="blue">One Way</Radio>
                      <Radio value="round-trip" colorScheme="blue">Round Trip</Radio>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {/* Round trip return details */}
                {formData.rideType === "round-trip" && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired>
                      <FormLabel>Return Date</FormLabel>
                      <Input
                        type="date"
                        value={formData.returnDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, returnDate: e.target.value }))}
                        variant="filled"
                        size="lg"
                        focusBorderColor="blue.500"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Return Time</FormLabel>
                      <Input
                        type="time"
                        value={formData.returnTime}
                        onChange={(e) => setFormData(prev => ({ ...prev, returnTime: e.target.value }))}
                        variant="filled"
                        size="lg"
                        focusBorderColor="blue.500"
                      />
                    </FormControl>
                  </SimpleGrid>
                )}

                {/* Departure Details */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl isRequired>
                    <FormLabel display="flex" alignItems="center" gap={2}>
                      <Icon as={FaCalendarAlt} color="blue.500" />
                      Departure Date
                    </FormLabel>
                    <Input
                      type="date"
                      value={formData.departureDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, departureDate: e.target.value }))}
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel display="flex" alignItems="center" gap={2}>
                      <Icon as={FaClock} color="blue.500" />
                      Departure Time
                    </FormLabel>
                    <Input
                      type="time"
                      value={formData.departureTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, departureTime: e.target.value }))}
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                    />
                  </FormControl>
                </SimpleGrid>

                {/* Pricing and Seats */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl isRequired>
                    <FormLabel>Price per Seat (₨)</FormLabel>
                    <NumberInput
                      min={0}
                      value={formatPrice(formData.price)}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          price: parsePrice(value),
                        }))
                      }
                      variant="filled"
                      size="lg"
                    >
                      <NumberInputField focusBorderColor="blue.500" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormHelperText>Price per passenger for this route</FormHelperText>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Available Seats</FormLabel>
                    <NumberInput
                      min={1}
                      max={7}
                      value={formData.availableSeats}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, availableSeats: parseInt(value) }))
                      }
                      variant="filled"
                      size="lg"
                    >
                      <NumberInputField focusBorderColor="blue.500" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormHelperText>How many passengers can you take?</FormHelperText>
                  </FormControl>
                </SimpleGrid>

                {/* Ride Preferences */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl>
                    <FormLabel>Luggage Space</FormLabel>
                    <Select
                      value={formData.luggageSpace}
                      onChange={(e) => setFormData(prev => ({ ...prev, luggageSpace: e.target.value }))}
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                    >
                      <option value="Small">Small (Carry-on only)</option>
                      <option value="Medium">Medium (1-2 bags)</option>
                      <option value="Large">Large (3+ bags)</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Rider Preferences</FormLabel>
                    <Stack spacing={2}>
                      <Checkbox 
                        isChecked={formData.smoking}
                        onChange={(e) => setFormData(prev => ({ ...prev, smoking: e.target.checked }))}
                        colorScheme="blue"
                      >
                        🚭 No Smoking
                      </Checkbox>
                      <Checkbox 
                        isChecked={formData.pets}
                        onChange={(e) => setFormData(prev => ({ ...prev, pets: e.target.checked }))}
                        colorScheme="blue"
                      >
                        🐾 Pets Allowed
                      </Checkbox>
                      <Checkbox 
                        isChecked={formData.femaleOnly}
                        onChange={(e) => setFormData(prev => ({ ...prev, femaleOnly: e.target.checked }))}
                        colorScheme="blue"
                      >
                        👩‍🦰 Female Only
                      </Checkbox>
                    </Stack>
                  </FormControl>
                </SimpleGrid>
              </VStack>
            ) : (
              // Sale/Rent Pricing Form
              <VStack spacing={6} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl isRequired>
                    <FormLabel>
                      {formData.type === "sale"
                        ? "Price (₨)"
                        : "Price Per Day (₨)"}
                    </FormLabel>
                    <NumberInput
                      min={0}
                      value={formatPrice(formData.price)}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          price: parsePrice(value),
                        }))
                      }
                      variant="filled"
                      size="lg"
                    >
                      <NumberInputField focusBorderColor="blue.500" />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  {formData.type === "sale" && (
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="offer" mb="0">
                        Offer Discount
                      </FormLabel>
                      <Switch
                        id="offer"
                        isChecked={formData.offer}
                        onChange={() =>
                          setFormData((prev) => ({
                            ...prev,
                            offer: !prev.offer,
                          }))
                        }
                        colorScheme="blue"
                      />
                    </FormControl>
                  )}

                  {formData.type === "sale" && formData.offer && (
                    <FormControl>
                      <FormLabel>Discounted Price (₨)</FormLabel>
                      <NumberInput
                        min={0}
                        max={formData.price}
                        value={formatPrice(formData.discountedPrice)}
                        onChange={(v) =>
                          setFormData((prev) => ({
                            ...prev,
                            discountedPrice: parsePrice(v),
                          }))
                        }
                        variant="filled"
                        size="lg"
                      >
                        <NumberInputField focusBorderColor="blue.500" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <FormHelperText>
                        Discount: ₨ {formData.price - formData.discountedPrice}
                      </FormHelperText>
                    </FormControl>
                  )}
                </SimpleGrid>

                {/* Sale Specific Options */}
                {formData.type === "sale" && (
                  <>
                    <Divider />
                    
                    {/* Installments Option */}
                    <Box>
                      <FormControl display="flex" alignItems="center" mb={4}>
                        <FormLabel htmlFor="installmentsAvailable" mb="0" fontSize="lg" fontWeight="semibold">
                          Offer Installment Plan
                        </FormLabel>
                        <Switch
                          id="installmentsAvailable"
                          colorScheme="green"
                          size="lg"
                          isChecked={formData.installmentsAvailable}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              installmentsAvailable: !prev.installmentsAvailable,
                            }))
                          }
                        />
                      </FormControl>

                      {formData.installmentsAvailable && (
                        <Card bg="green.50" p={4} borderRadius="lg" border="1px solid" borderColor="green.200">
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl isRequired>
                              <FormLabel>Down Payment (₨)</FormLabel>
                              <NumberInput
                                min={0}
                                value={formatPrice(formData.downPayment)}
                                onChange={(value) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    downPayment: parsePrice(value),
                                  }))
                                }
                                variant="filled"
                                size="lg"
                              >
                                <NumberInputField focusBorderColor="green.500" />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <FormHelperText>Initial payment required</FormHelperText>
                            </FormControl>

                            <FormControl isRequired>
                              <FormLabel>Monthly Installment (₨)</FormLabel>
                              <NumberInput
                                min={0}
                                value={formatPrice(formData.monthlyInstallment)}
                                onChange={(value) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    monthlyInstallment: parsePrice(value),
                                  }))
                                }
                                variant="filled"
                                size="lg"
                              >
                                <NumberInputField focusBorderColor="green.500" />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <FormHelperText>Monthly payment amount</FormHelperText>
                            </FormControl>

                            <FormControl>
                              <FormLabel>Installment Period (months)</FormLabel>
                              <Select
                                value={formData.installmentPeriod}
                                onChange={(e) => setFormData(prev => ({ ...prev, installmentPeriod: parseInt(e.target.value) }))}
                                variant="filled"
                                size="lg"
                                focusBorderColor="green.500"
                              >
                                <option value={3}>3 months</option>
                                <option value={6}>6 months</option>
                                <option value={12}>12 months</option>
                                <option value={18}>18 months</option>
                                <option value={24}>24 months</option>
                                <option value={36}>36 months</option>
                                <option value={48}>48 months</option>
                                <option value={60}>60 months</option>
                              </Select>
                            </FormControl>
                          </SimpleGrid>
                        </Card>
                      )}
                    </Box>

                    <Divider />

                    {/* Exchange Option */}
                    <Box>
                      <FormControl display="flex" alignItems="center" mb={4}>
                        <FormLabel htmlFor="exchangeAvailable" mb="0" fontSize="lg" fontWeight="semibold">
                          Accept Exchange
                        </FormLabel>
                        <Switch
                          id="exchangeAvailable"
                          colorScheme="purple"
                          size="lg"
                          isChecked={formData.exchangeAvailable}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              exchangeAvailable: !prev.exchangeAvailable,
                            }))
                          }
                        />
                      </FormControl>

                      {formData.exchangeAvailable && (
                        <Card bg="purple.50" p={4} borderRadius="lg" border="1px solid" borderColor="purple.200">
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl isRequired>
                              <FormLabel display="flex" alignItems="center" gap={2}>
                                <Icon as={FaCar} color="purple.600" />
                                Looking for Manufacturer
                              </FormLabel>
                              <Select
                                id="exchangeManufacturer"
                                value={formData.exchangeManufacturer}
                                onChange={changeHandler}
                                variant="filled"
                                size="lg"
                                focusBorderColor="purple.500"
                                placeholder="Select manufacturer"
                              >
                                {carsData.map((car) => (
                                  <option key={car.manufacturer} value={car.manufacturer}>
                                    {car.manufacturer}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>

                            <FormControl>
                              <FormLabel>Model (Optional)</FormLabel>
                              <Input
                                id="exchangeModel"
                                value={formData.exchangeModel}
                                onChange={changeHandler}
                                placeholder="e.g., Civic, Corolla"
                                variant="filled"
                                size="lg"
                                focusBorderColor="purple.500"
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel>Preferred Year</FormLabel>
                              <NumberInput
                                min={1920}
                                max={2024}
                                value={formData.exchangeYear}
                                onChange={(value) => setFormData(prev => ({ ...prev, exchangeYear: value }))}
                                variant="filled"
                                size="lg"
                              >
                                <NumberInputField placeholder="Any year" focusBorderColor="purple.500" />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                            </FormControl>

                            <FormControl>
                              <FormLabel>Max Mileage (km)</FormLabel>
                              <NumberInput
                                min={0}
                                value={formData.exchangeMileage}
                                onChange={(value) => setFormData(prev => ({ ...prev, exchangeMileage: value }))}
                                variant="filled"
                                size="lg"
                              >
                                <NumberInputField placeholder="Any mileage" focusBorderColor="purple.500" />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                            </FormControl>

                            <FormControl>
                              <FormLabel>Minimum Condition</FormLabel>
                              <Select
                                id="exchangeCondition"
                                value={formData.exchangeCondition}
                                onChange={changeHandler}
                                variant="filled"
                                size="lg"
                                focusBorderColor="purple.500"
                              >
                                <option value="excellent">Excellent</option>
                                <option value="good">Good</option>
                                <option value="fair">Fair</option>
                              </Select>
                            </FormControl>
                          </SimpleGrid>
                          <Alert status="info" mt={4} size="sm" borderRadius="md">
                            <AlertIcon />
                            <Text fontSize="sm">Let buyers know what car you're looking for in exchange</Text>
                          </Alert>
                        </Card>
                      )}
                    </Box>
                  </>
                )}
              </VStack>
            )}
          </VStack>
        );

      case 2:
        return (
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="lg" bg="blue.50" borderLeft="4px solid" borderLeftColor="blue.500">
              <AlertIcon color="blue.500" />
              {formData.type === "ride" 
                ? "Specify the exact pickup location in the pickup city" 
                : "Tell buyers where they can find the car"}
            </Alert>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {formData.type === "ride" ? (
                <>
                  {/* For Ride: Show pickup city (read-only) and pickup location input */}
                  <FormControl>
                    <FormLabel display="flex" alignItems="center" gap={2}>
                      <Icon as={FaMapMarkerAlt} color="blue.500" />
                      Pickup City
                    </FormLabel>
                    <Input
                      value={formData.fromCity || "Not selected yet"}
                      isReadOnly
                      variant="filled"
                      size="lg"
                      bg="gray.100"
                    />
                    <FormHelperText>Auto-set from your selected route</FormHelperText>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel display="flex" alignItems="center" gap={2}>
                      <Icon as={FaMapMarkerAlt} color="blue.500" />
                      Exact Pickup Location
                    </FormLabel>
                    <Input
                      id="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={changeHandler}
                      placeholder="e.g., Main Bus Stand, City Mall Entrance, Shell Petrol Pump, etc."
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                    />
                    <FormHelperText>Be specific - this helps passengers find you easily</FormHelperText>
                  </FormControl>
                </>
              ) : (
                <>
                  {/* For Sale/Rent: Show city and location as before */}
                  <FormControl isRequired>
                    <FormLabel>City</FormLabel>
                    <Select
                      id="city"
                      value={formData.city}
                      onChange={changeHandler}
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                    >
                      {cities.map((city) => (
                        <option key={city.city} value={city.city}>
                          {city.city}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Location Details</FormLabel>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={changeHandler}
                      placeholder="e.g., Main Street, near City Mall"
                      variant="filled"
                      size="lg"
                      focusBorderColor="blue.500"
                    />
                    <FormHelperText>Be specific about the area or neighborhood</FormHelperText>
                  </FormControl>
                </>
              )}
            </SimpleGrid>

            <FormControl>
              <FormLabel>Additional Notes</FormLabel>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={changeHandler}
                placeholder={formData.type === "ride"
                  ? "Any additional information about the ride, meeting arrangements, special requests..."
                  : formData.type === "sale" && formData.exchangeAvailable
                  ? "Tell potential buyers about your exchange preferences, what you're looking for, etc..."
                  : "Any additional information about the car or meeting arrangements..."}
                variant="filled"
                size="lg"
                minH="120px"
                focusBorderColor="blue.500"
              />
              <FormHelperText>
                Optional: Include meeting preferences, availability, etc.
              </FormHelperText>
            </FormControl>
          </VStack>
        );

      case 3:
        return (
          <VStack spacing={6} align="stretch">
            <Alert status="info" borderRadius="lg" bg="blue.50" borderLeft="4px solid" borderLeftColor="blue.500">
              <AlertIcon color="blue.500" />
              Add features and images to make your listing stand out
            </Alert>

            {formData.type !== "ride" && (
              <FormControl>
                <FormLabel>Features & Options</FormLabel>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {Object.entries(formData.options).map(([key, value]) => (
                    <Checkbox
                      key={key}
                      isChecked={value}
                      onChange={() =>
                        setFormData((prev) => ({
                          ...prev,
                          options: {
                            ...prev.options,
                            [key]: !prev.options[key],
                          },
                        }))
                      }
                      colorScheme="blue"
                      size="lg"
                    >
                      <Text textTransform="capitalize">
                        {key.replace(/([A-Z])/g, " $1")}
                      </Text>
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </FormControl>
            )}

            <Divider />

            <FormControl isRequired>
              <FormLabel>Vehicle Images</FormLabel>
              <Card border="2px dashed" borderColor="gray.300" p={6} borderRadius="lg">
                <CardBody textAlign="center">
                  <input
                    type="file"
                    id="images"
                    accept=".jpg,.png,.jpeg"
                    onChange={changeHandler}
                    multiple
                    style={{ display: "none" }}
                  />
                  <Button
                    as="label"
                    htmlFor="images"
                    colorScheme="blue"
                    variant="outline"
                    size="lg"
                    leftIcon={<FaImage />}
                    cursor="pointer"
                    w="full"
                  >
                    Choose Images
                  </Button>
                  <Text mt={4} color="gray.600">
                    {formData.images && Object.keys(formData.images).length > 0
                      ? `${Object.keys(formData.images).length} image(s) selected`
                      : "No images selected"}
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    The first image will be the cover (max 6 images)
                  </Text>
                </CardBody>
              </Card>
              {formData.images && Object.keys(formData.images).length > 0 && (
                <Progress
                  value={(Object.keys(formData.images).length / 6) * 100}
                  size="sm"
                  colorScheme="blue"
                  mt={4}
                  borderRadius="full"
                />
              )}
            </FormControl>

            <Alert status="warning" borderRadius="lg" mt={6}>
              <AlertIcon />
              <Text>
                <strong>Important:</strong> Clear images attract more passengers/buyers.
                Make sure to include pictures of the interior, exterior, and
                any special features.
              </Text>
            </Alert>
          </VStack>
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>Post Listing | NCPWheels</title>
      </Helmet>

      <Box bg="gray.50" minH="100vh" py={8}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <Box textAlign="center">
              <Heading size="xl" color="blue.700" mb={2}>
                Post Your Vehicle
              </Heading>
              <Text color="gray.600" fontSize="lg">
                {formData.type === "ride" 
                  ? "Share your ride and split costs with fellow travelers" 
                  : "Fill in the details below to list your vehicle"}
              </Text>
            </Box>

            {/* Progress Stepper */}
            <Card variant="elevated" p={{ base: 4, md: 6 }} borderRadius="2xl">
              <CardBody>
                <Stepper
                  index={activeStep}
                  size="lg"
                  colorScheme="blue"
                  orientation={isMobile ? "vertical" : "horizontal"}
                >
                  {steps.map((step, index) => (
                    <Step key={index}>
                      <StepIndicator>
                        <StepStatus
                          complete={<StepNumber />}
                          incomplete={<StepNumber />}
                          active={<Icon as={step.icon} boxSize={5} />}
                        />
                      </StepIndicator>

                      <Box flexShrink="0">
                        <StepTitle>{step.title}</StepTitle>
                        <StepDescription>{step.description}</StepDescription>
                      </Box>

                      <StepSeparator />
                    </Step>
                  ))}
                </Stepper>
              </CardBody>
            </Card>

            {/* Current Step Content */}
            <Card variant="elevated" borderRadius="2xl">
              <CardBody p={{ base: 4, md: 8 }}>
                <form onSubmit={submitHandler}>
                  {renderStepContent()}

                  {/* Navigation Buttons */}
                  <HStack justify="space-between" mt={8} pt={6} borderTop="1px solid" borderColor="gray.200">
                    <Button
                      leftIcon={<FaArrowLeft />}
                      onClick={prevStep}
                      isDisabled={activeStep === 0}
                      variant="outline"
                      colorScheme="blue"
                      size="lg"
                    >
                      Previous
                    </Button>

                    <Box>
                      <Badge colorScheme="blue" fontSize="md" p={2} borderRadius="full">
                        Step {activeStep + 1} of {steps.length}
                      </Badge>
                    </Box>

                    {activeStep < steps.length - 1 ? (
                      <Button
                        rightIcon={<FaArrowRight />}
                        onClick={nextStep}
                        colorScheme="blue"
                        size="lg"
                      >
                        Next Step
                      </Button>
                    ) : (
                      <Button
                        colorScheme="blue"
                        size="lg"
                        type="submit"
                        isLoading={buttonLoading}
                        loadingText="Submitting..."
                        rightIcon={formData.type === "ride" ? <FaUsers /> : <FaCar />}
                      >
                        {formData.type === "ride" ? "Post Ride" : "Submit Listing"}
                      </Button>
                    )}
                  </HStack>
                </form>
              </CardBody>
            </Card>

            {/* Quick Tips */}
            <Card variant="filled" bg="blue.50" borderColor="blue.200" borderRadius="2xl">
              <CardBody>
                <Heading size="md" color="blue.700" mb={3}>
                  💡 Tips for a Successful Listing
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <Box>
                    <Text fontWeight="bold" color="blue.600">High-Quality Photos</Text>
                    <Text fontSize="sm">Use clear, well-lit images from multiple angles</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" color="blue.600">Accurate Details</Text>
                    <Text fontSize="sm">Be honest about car condition and features</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" color="blue.600">Clear Pickup Location</Text>
                    <Text fontSize="sm">Provide specific landmark for easy meeting</Text>
                  </Box>
                </SimpleGrid>
              </CardBody>
            </Card>
          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default CreateListing;